import * as React from 'react';
import { cn } from '@/utils/cn';

export interface TeeDigitInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  label?: string;
  hint?: string;
  destructive?: boolean;
  autoFocus?: boolean;
}

const TeeDigitInput = React.forwardRef<HTMLDivElement, TeeDigitInputProps>(
  (
    {
      length = 6,
      value = '',
      onChange,
      onComplete,
      disabled = false,
      placeholder = '•',
      className,
      inputClassName,
      label,
      hint,
      destructive = false,
      autoFocus = true,
    },
    ref
  ) => {
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

    React.useEffect(() => {
      if (autoFocus && inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, [autoFocus]);

    const digits = value
      .split('')
      .concat(Array(length - value.length).fill(''));

    const handleChange = (index: number, val: string) => {
      const numericVal = val.replace(/[^0-9]/g, '');

      if (numericVal.length > 1) {
        // Paste detected: take only the digits we need from current index onwards
        const remainingLength = length - index;
        const pastedValue = numericVal.slice(0, remainingLength);
        const newValue = value.slice(0, index) + pastedValue;
        const finalValue = newValue.slice(0, length);
        onChange?.(finalValue);

        // Focus on the last filled input or the next empty one
        const nextIndex = Math.min(index + pastedValue.length, length - 1);
        setTimeout(() => inputRefs.current[nextIndex]?.focus(), 0);

        if (finalValue.length === length) {
          onComplete?.(finalValue);
        }
        return;
      }

      const newValue =
        value.slice(0, index) + numericVal + value.slice(index + 1);
      onChange?.(newValue);

      if (numericVal && index < length - 1) {
        setTimeout(() => inputRefs.current[index + 1]?.focus(), 0);
      }

      if (
        newValue.length === length &&
        newValue.replace(/\s/g, '').length === length
      ) {
        onComplete?.(newValue);
      }
    };

    const handleKeyDown = (
      index: number,
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (e.key === 'Backspace') {
        if (value[index]) {
          const newValue = value.slice(0, index) + value.slice(index + 1);
          onChange?.(newValue);
        } else if (index > 0) {
          setTimeout(() => inputRefs.current[index - 1]?.focus(), 0);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handlePaste = (
      index: number,
      e: React.ClipboardEvent<HTMLInputElement>
    ) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const numericVal = pastedText.replace(/[^0-9]/g, '');

      if (numericVal.length > 0) {
        const remainingLength = length - index;
        const pastedValue = numericVal.slice(0, remainingLength);
        const newValue = value.slice(0, index) + pastedValue;
        const finalValue = newValue.slice(0, length);
        onChange?.(finalValue);

        const nextIndex = Math.min(index + pastedValue.length, length - 1);
        setTimeout(() => inputRefs.current[nextIndex]?.focus(), 0);

        if (finalValue.length === length) {
          onComplete?.(finalValue);
        }
      }
    };

    const labelClasses = cn(
      "font-[family-name:var(--type-font-family-body,'Urbanist:medium',sans-serif)]",
      'font-medium',
      'text-body-s',
      'text-neutral-600',
      destructive && 'text-red-500'
    );

    const hintClasses = cn(
      'font-[family-name:var(--type-font-family-body,"Urbanist:medium",sans-serif)]',
      'font-medium',
      'text-body-xs',
      'text-neutral-600',
      destructive && 'text-red-500'
    );

    const digitInputClasses = cn(
      'w-10 h-12 text-center text-lg font-semibold',
      'border border-solid rounded-[var(--spacing-xs)]',
      'bg-base-white text-base-black',
      'placeholder:text-neutral-400',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-0',
      destructive
        ? 'border-red-500 focus-visible:ring-red-500'
        : 'border-neutral-300 focus-visible:ring-base-black',
      disabled && 'bg-[var(--background-input-field-disabled)] opacity-50 cursor-not-allowed',
      'transition-all duration-200',
      inputClassName
    );

    return (
      <div ref={ref} className={cn('flex w-full flex-col gap-1.5', className)}>
        {label && <label className={labelClasses}>{label}</label>}

        <div className="flex gap-2 justify-center">
          {Array.from({ length }).map((_, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digits[index] || ''}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              disabled={disabled}
              placeholder={placeholder}
              className={digitInputClasses}
              aria-label={`Digit ${index + 1}`}
            />
          ))}
        </div>

        {hint && (
          <div className="flex gap-1 items-start justify-center">
            <div className="flex shrink-0 items-center mt-0.5">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="text-neutral-600"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="7"
                  stroke="currentColor"
                  strokeWidth="1"
                />
                <text
                  x="8"
                  y="10"
                  textAnchor="middle"
                  fontSize="10"
                  fill="currentColor"
                  fontWeight="bold"
                >
                  i
                </text>
              </svg>
            </div>
            <p className={hintClasses}>{hint}</p>
          </div>
        )}
      </div>
    );
  }
);

TeeDigitInput.displayName = 'TeeDigitInput';

export { TeeDigitInput };
